import { Phone } from "lucide-react";
import { trackPhoneCall } from "../lib/analytics";

const NUMBER_DISPLAY = "(888) 773-8677";
const NUMBER_HREF = "tel:+18887738677";
const ARIA = "Call VidaVital Medical at 888-773-8677";

/**
 * Small inline phone link.
 * Props: location (required, string), variant ('inline' | 'button' | 'gold')
 */
export function PhoneLink({ location, variant = "inline", className = "", showIcon = true, testId }) {
  const base = "inline-flex items-center gap-1.5 font-semibold transition-colors";
  const variants = {
    inline: "text-[color:var(--vv-teal)] hover:text-[color:var(--vv-teal-hover)]",
    button: "vv-btn-primary",
    gold:   "vv-btn-gold",
    dark:   "text-white hover:text-[color:var(--vv-teal)]",
  };
  return (
    <a
      href={NUMBER_HREF}
      onClick={() => trackPhoneCall(location)}
      aria-label={ARIA}
      data-testid={testId || `phone-link-${location}`}
      className={`${base} ${variants[variant] || variants.inline} ${className}`}
    >
      {showIcon && <Phone size={variant === "button" || variant === "gold" ? 16 : 15} />}
      {NUMBER_DISPLAY}
    </a>
  );
}

/**
 * Full "Call VidaVital Medical" card — used on Schedule, therapy pages, questionnaire results.
 */
export function CallCard({ location, title, subtitle, tone = "teal", testId }) {
  const tones = {
    teal: "bg-amber-50 border-amber-100",
    gold: "bg-amber-50 border-amber-100",
    dark: "bg-stone-900 border-stone-800 text-white",
  };
  const isDark = tone === "dark";
  return (
    <a
      href={NUMBER_HREF}
      onClick={() => trackPhoneCall(location)}
      aria-label={ARIA}
      data-testid={testId || `call-card-${location}`}
      className={`block rounded-2xl border p-6 md:p-7 transition-transform hover:-translate-y-0.5 ${tones[tone]}`}
    >
      <div className={`text-xs uppercase tracking-widest ${isDark ? "text-amber-300" : "text-[color:var(--vv-teal)]"} font-semibold`}>
        {title || "Prefer to speak with someone?"}
      </div>
      <div className={`mt-2 text-sm ${isDark ? "text-stone-300" : "text-stone-600"} max-w-md`}>
        {subtitle || "Questions about treatment options, bloodwork, or scheduling? Our patient care coordinators are here to help."}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <span className={`w-10 h-10 rounded-full grid place-items-center ${isDark ? "bg-amber-500/20" : "bg-white"}`}>
          <Phone className={isDark ? "text-amber-300" : "text-[color:var(--vv-teal)]"} size={18} />
        </span>
        <div>
          <div className={`font-display text-2xl md:text-3xl ${isDark ? "text-white" : "text-stone-900"}`}>{NUMBER_DISPLAY}</div>
          <div className={`text-xs uppercase tracking-widest ${isDark ? "text-stone-400" : "text-stone-500"}`}>Click to call</div>
        </div>
      </div>
    </a>
  );
}

/**
 * Floating mobile call button, always visible on public pages.
 */
export function FloatingCallButton() {
  return (
    <a
      href={NUMBER_HREF}
      onClick={() => trackPhoneCall("floating_mobile")}
      aria-label={ARIA}
      data-testid="floating-call-button"
      className="fixed bottom-5 right-5 z-50 xl:hidden w-14 h-14 rounded-full bg-[color:var(--vv-gold)] text-[color:var(--vv-gold-deeper)] grid place-items-center shadow-[0_10px_30px_rgba(216,182,106,0.55)] hover:bg-[color:var(--vv-gold-hover)] transition-colors"
    >
      <Phone size={22} />
    </a>
  );
}

/**
 * Persistent desktop "Call" pill in the lower-right corner.
 */
export function StickyDesktopCTA() {
  return (
    <div className="hidden xl:block fixed bottom-6 right-6 z-40 group">
      <a
        href={NUMBER_HREF}
        onClick={() => trackPhoneCall("sticky_desktop")}
        aria-label={ARIA}
        data-testid="sticky-desktop-cta"
        className="flex items-center gap-2 bg-[color:var(--vv-gold)] text-[color:var(--vv-gold-deeper)] pl-4 pr-5 py-3 rounded-full shadow-[0_10px_30px_rgba(216,182,106,0.5)] hover:bg-[color:var(--vv-gold-hover)] transition-colors"
      >
        <Phone size={16} />
        <span className="text-sm font-semibold">{`Call ${NUMBER_DISPLAY}`}</span>
      </a>
      <div className="absolute bottom-full right-0 mb-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="bg-stone-900 text-white text-xs px-3 py-2 rounded-lg shadow whitespace-nowrap">
          Questions? We&apos;re here to help.
        </div>
      </div>
    </div>
  );
}

export const PHONE = { display: NUMBER_DISPLAY, href: NUMBER_HREF, aria: ARIA };
